import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { App, Button, Card, Col, Descriptions, Input, Modal, Popconfirm, Row, Select, Skeleton, Space, Tabs, Tag, Tooltip, Typography } from 'antd';
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { CardTitle } from '../../components/StatCard';
import { palette } from '../../app/theme';
import type { FieldDefinitionDto } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { Timeline } from '../../components/Timeline';
import { TaskList } from '../../components/TaskList';
import { formatValue } from '../../components/DynamicField';
import { readValue } from '../../features/fields/system-keys';
import { useFields } from '../../features/fields/api';
import { dealContactName, money, useDeal, useDeleteDeals, useMoveDeal, usePipelines } from '../../features/deals/api';
import { errorMessage } from '../../api/client';
import { DealDrawer } from './DealDrawer';
import { PageSkeleton, RecordError } from '../../components/states';
import { fmtDate } from '../../utils/format';
import { splitFieldGroups, ViewMoreCard } from '../../components/ViewMore';
import { EntityTag } from '../../components/EntityTag';

const KIND_DOT = { OPEN: palette.primary, WON: palette.success, LOST: palette.error } as const;
const KIND_TAG = { OPEN: 'blue', WON: 'success', LOST: 'error' } as const;

export function DealPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const deal = useDeal(id);
  const defs = useFields('DEAL');
  const pipelines = usePipelines();
  const move = useMoveDeal();
  const remove = useDeleteDeals();
  const [editing, setEditing] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);
  const [lostReason, setLostReason] = useState('');

  const groups = useMemo(() => splitFieldGroups(defs.data ?? [], (f) => ['title', 'pipeline_id', 'stage_id'].includes(f.key)), [defs.data]);

  if (deal.isError) return <RecordError error={deal.error} what="Deal" backTo="/pipeline" backLabel="Back to pipeline" onRetry={deal.refetch} />;
  if (!deal.data) return <PageSkeleton />;
  const d = deal.data;
  const stages = pipelines.data?.find((p) => p.id === d.pipelineId)?.stages ?? [];
  const won = stages.find((s) => s.kind === 'WON');
  const lost = stages.find((s) => s.kind === 'LOST');
  /** Resolves true when the move succeeded, so callers can close dialogs only on success. */
  const doMove = (stageId: string, reason?: string) =>
    move
      .mutateAsync({ id: d.id, stageId, lostReason: reason })
      .then(() => {
        message.success('Deal moved');
        return true;
      })
      .catch((e) => {
        message.error(errorMessage(e));
        return false;
      });
  // while pipelines load, show the current stage by name rather than its id
  const stageOptions = stages.length ? stages.map((s) => ({ value: s.id, label: s.name })) : [{ value: d.stageId, label: d.stage.name }];

  const cell = (f: FieldDefinitionDto) => {
    switch (f.key) {
      case 'contact_id': return d.contact ? <EntityTag kind="contact" id={d.contact.id}>{dealContactName(d.contact)}</EntityTag> : '';
      case 'organization_id': return d.organization ? <EntityTag kind="organization" id={d.organization.id}>{d.organization.name}</EntityTag> : '';
      case 'agent_id': return d.agent?.name ?? '';
      case 'value': return money(d.value, d.currency);
      case 'expected_close_at': return d.expectedCloseAt ? fmtDate(d.expectedCloseAt) : '';
      default: return formatValue(f, readValue(f, d as unknown as Record<string, unknown>));
    }
  };

  const renderFields = (fields: FieldDefinitionDto[]) => (
    <Descriptions className="field-list" column={1} size="small" colon={false} styles={{ label: { width: 150, color: palette.textSecondary } }}>
      {fields.map((f) => (
        <Descriptions.Item key={f.key} label={f.label}>{cell(f) || <Typography.Text type="secondary">—</Typography.Text>}</Descriptions.Item>
      ))}
    </Descriptions>
  );

  return (
    <div className="page">
      <PageHeader
        breadcrumb={[{ title: 'Pipeline', to: '/pipeline' }, { title: d.pipeline.name, to: `/pipeline?pipeline=${d.pipelineId}` }, { title: d.title }]}
        title={d.title}
        subtitle={
          <Space size={8} wrap>
            <Tag color={KIND_TAG[d.stage.kind]} style={{ marginInlineEnd: 0 }}>
              <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 4, marginRight: 6, background: d.stage.color ?? KIND_DOT[d.stage.kind] }} />
              {d.stage.name}
            </Tag>
            <Typography.Text strong>{money(d.value, d.currency)}</Typography.Text>
            <Typography.Text type="secondary">{d.pipeline.name}</Typography.Text>
            {d.closedAt && <Typography.Text type="secondary">Closed {fmtDate(d.closedAt)}</Typography.Text>}
            {d.lostReason && <Typography.Text type="danger">{d.lostReason}</Typography.Text>}
          </Space>
        }
        extra={
          <Space>
            <Select size="middle" aria-label="Stage" value={d.stageId} style={{ minWidth: 160 }} loading={move.isPending || pipelines.isLoading} disabled={move.isPending} onChange={(v) => void doMove(v)} options={stageOptions} />
            {won && d.stage.kind !== 'WON' && <Button icon={<CheckOutlined />} style={{ color: palette.success, borderColor: palette.success }} loading={move.isPending && move.variables?.stageId === won.id} onClick={() => void doMove(won.id)}>Won</Button>}
            {lost && d.stage.kind !== 'LOST' && <Button icon={<CloseOutlined />} danger onClick={() => { setLostReason(''); setLostOpen(true); }}>Lost</Button>}
            <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>Edit</Button>
            <Popconfirm title="Delete this deal?" okText="Delete" okButtonProps={{ danger: true, loading: remove.isPending }} onConfirm={() => remove.mutateAsync([d.id]).then(() => { message.success('Deal deleted'); navigate('/pipeline'); }).catch((e) => message.error(errorMessage(e)))}>
              <Tooltip title="Delete deal">
                <Button danger icon={<DeleteOutlined />} aria-label="Delete deal" />
              </Tooltip>
            </Popconfirm>
          </Space>
        }
      />
      <Row gutter={24}>
        <Col xs={24} lg={9}>
          {defs.isLoading && <Card style={{ marginBottom: 24 }}><Skeleton active /></Card>}
          {groups.main.map(([group, fields]) => (
            <Card key={group} title={<CardTitle title={group} />} style={{ marginBottom: 24 }}>
              {renderFields(fields)}
            </Card>
          ))}
          <ViewMoreCard groups={groups.more} render={renderFields} />
        </Col>
        <Col xs={24} lg={15}>
          <Card>
            <Tabs
              items={[
                { key: 'timeline', label: 'Timeline', children: <Timeline dealId={d.id} fields={defs.data} showContact /> },
                { key: 'tasks', label: `Tasks${d.counts.openTasks ? ` (${d.counts.openTasks})` : ''}`, children: <TaskList dealId={d.id} /> },
              ]}
            />
          </Card>
        </Col>
      </Row>
      <DealDrawer open={editing} onClose={() => setEditing(false)} deal={d} />
      <Modal
        title="Mark as lost"
        open={lostOpen}
        onCancel={() => setLostOpen(false)}
        okText="Mark lost"
        okButtonProps={{ danger: true }}
        confirmLoading={move.isPending}
        onOk={async () => {
          if (lost && (await doMove(lost.id, lostReason || undefined))) setLostOpen(false);
        }}
      >
        <Input.TextArea value={lostReason} onChange={(e) => setLostReason(e.target.value)} placeholder="Reason (optional)" autoSize={{ minRows: 2 }} maxLength={500} />
      </Modal>
    </div>
  );
}
